import React, {useEffect, useState} from 'react';
import API from '../../../app/api';
import {useDispatch} from 'react-redux';
import {setPatient} from '../../../store/reducers/patient';
import _ from 'lodash';
import makeStyles from '@mui/styles/makeStyles';
import InformationBox from '../../../components/other/informationBox';
import {useTranslation} from 'react-i18next';
import AgyleLoadingSpinner from '../../../components/other/agyleLoadingSpinner';
import clsx from 'clsx';
import AgyleToggleButtons from '../../../components/ui/buttons/agyleToggleButtons';
import {AgyleNamedIcon} from '../../../components/icons/agyle';
import ActivitySpellSelector from '../../../components/notes/component/activitySpellSelector';
import HistAttendanceField from './histAttendanceField';
import {BASE_TAB, setCurrentTab, setHistoryPatient} from '../../../store/reducers/patientContext';
import { sendUserAudit } from '../../../store/reducers/user';
import { AUDIT_TYPES } from '../../../util/auditUtil';

export const useHistStyles = makeStyles((theme) => ({
    root: {
        height: '100%',
    },
    hover: {
        padding: 0,
        marginBottom: 10,
        '&:hover': {
            backgroundColor: 'unset',
        },
    },
    attendanceListItem: {
        width: '100%',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: theme.palette.inPatient.formBackground,
        marginBottom: 10,
    },
    attendanceHeader: {
        minHeight: 35,
        padding: 5,
        paddingLeft: 15,
    },
    attendanceDetails: {
        padding: 10,
        paddingLeft: 15,
        backgroundColor: theme.palette.background.paperDarker,
        fontSize: 14,
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        '&.right': {
            marginLeft: 'auto',
        },
    },
    label: {
        fontFamily: 'Inter',
        fontWeight: 'bold',
        marginRight: 5,
        fontSize: 14,
    },
    referral: {
        display: 'inline-block',
        width: 43,
        margin: 1,
        height: 25,
        textAlign: 'center',
        lineHeight: '25px',
        color: '#fff',
        borderRadius: 10,
        fontSize: 'small',
        fontWeight: 'bold',
        marginRight: 5,
        '&.completed': {
            border: '2px solid #000',
            color: '#000',
            lineHeight: '20px',
        },
    },
    avatarCustom: {
        width: 'auto !important',
        paddingLeft: 10,
        paddingRight: 10,
        borderRadius: 15,
        backgroundColor: theme.palette.background.paper,
        color: '#fff',
    },
    panel: {
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '16px 10px',
        alignItems: 'center',
        gap: 10,
        height: 55,
        lineHeight: 1,
    },
    discriminator: {
        borderRadius: 6,
        padding: '2px 6px',
    },
    list: {
        height: 'calc(100% - 70px)',
        overflowY: 'auto',
        margin: '0 10px',
    },
    toggleRoot: {
        color: theme.palette.mode === 'dark' ? '#222222' : '#f3f3f4',
        backgroundColor: theme.palette.mode === 'dark' ? '#313233' : '#eff1f3',
        display: 'inline-block',
        width: '100%',
        padding: '5px !important',
    },
    toggleLabel: {
        color: theme.palette.mode === 'dark' ? '#ACAFB3' : '#222222',
        height: '25px !important',
        fontSize: 12,
        '&.icon': {
            fontSize: 10,
        },
        '&.Mui-disabled': {
            border: 'none',
        },
    },
    toggleSelected: {
        color: theme.palette.mode === 'dark' ? '#222222 !important' : '#ffffff !important',
        fontWeight: 700,
        backgroundColor: theme.palette.mode === 'dark' ? '#ffffff !important' : '#626466 !important',
    },
    headerText: {
        color: theme.palette.admissionForm.labelColor,
    },
    block: {
        padding: '5px 10px',
    },
}));

function InpatientHistoryList({patient}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const classes = useHistStyles();

    const admissionId = patient?.admission?.__id;

    const [attendances, setAttendances] = useState([]);

    // Filters
    const [expandAll, setExpandAll] = useState(false);
    const [selectedAttendance, setSelectedAttendance] = useState(null);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadAttendance();
    }, [patient]);

    const loadAttendance = () => {
        API.getPatientAttendancesHistory(patient?.patient?.__id)
            .then((result) => {
                setAttendances(result);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const openItem = (param, disabled) => {
        if (!disabled && param.patient) {
            dispatch(sendUserAudit(param, AUDIT_TYPES.OPEN_HISTORICAL_ACTIVITY));
            dispatch(setPatient(param.patient));
            dispatch(setHistoryPatient(param));
            dispatch(setCurrentTab(BASE_TAB));
        }
    };

    const getFilteredAtt = () => {
        if (selectedAttendance) {
            return _.filter(attendances, att => att?.admission?.__id === selectedAttendance?.__id) || [];
        }
        return attendances;
    };

    return (
        <div className={classes.root}>
            <div className={classes.panel}>
                <div>
                    <ActivitySpellSelector
                        type="activity"
                        admissions={attendances?.map(n => n.admission)}
                        setCurrentActivity={setSelectedAttendance}
                    />
                </div>

                <div style={{width: 100}}>
                    <AgyleToggleButtons
                        classNameRoot={clsx(classes.toggleRoot, 'header')}
                        classNameLabel={clsx(classes.toggleLabel, 'icon')}
                        classNameSelected={classes.toggleSelected}
                        onChange={(val) => {
                            if (_.isBoolean(val)) {
                                setExpandAll(val);
                            }
                        }}
                        value={expandAll}
                        size="small"
                        options={[
                            {
                                value: false,
                                label: <AgyleNamedIcon className={classes.icon} name="collapseAll"/>,
                            },
                            {
                                value: true,
                                label: <AgyleNamedIcon className={classes.icon} name="expandAll"/>,
                            },
                        ]}
                        fullWidth
                    />
                </div>
            </div>

            {loading && <AgyleLoadingSpinner/>}

            {!loading && _.isEmpty(attendances) &&
                <InformationBox type="info" style={{margin: '0 10px', width: 'auto'}}>
                    {t('This patient has no historical activities.')}
                </InformationBox>
            }

            <div className={clsx(classes.list, 'scroll-normal-white')}>
                {!loading && getFilteredAtt()?.map(a => (
                    <HistAttendanceField
                        key={a.admission.__id}
                        admission={a}
                        expandFromParent={expandAll}
                        onOpen={openItem}
                        disabledAdmission={admissionId === a.admission.__id}
                    />
                ))}
            </div>
        </div>
    );
}

export default InpatientHistoryList;